/**
 * Pilot testing (Bill 2026-11-04): the pilot test workbook, worked here instead of in spreadsheets passed around.
 *
 * Sections are the workbook's tester tabs (UAT - Consumer, UAT - Business, UAT - Trust, UAT - Safe Deposit Box, Test
 * Scenarios, Test Results); Risk Management's own tabs are in the Risk panel. Business, Trust and Safe Deposit Box are
 * Phase 2: listed, labelled "Phase 2 — not in the consumer pilot", and closed until opened. Each section shows the
 * workbook's Summary counts; the filters and search work across all of them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { PilotBoard, PilotItem, PilotSection } from './pilotApi';
import {
  EMPTY_PILOT_FILTER,
  NO_STATUS,
  filterItems,
  isAssignedTo,
  itemHeadline,
  shortDay,
  startsCollapsed,
  statusTone,
  summaryTotals,
  testerGroups,
  type PilotFilter,
} from './pilotModel';
import { PilotItemDialog } from './PilotItemDialog';
import { PilotIssueDialog } from './PilotIssueDialog';
import { AddItemDialog } from './AddItemDialog';

export function PilotTestingTab(): JSX.Element {
  const client = useApiClient();
  const [board, setBoard] = useState<PilotBoard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState<PilotFilter>(EMPTY_PILOT_FILTER);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [issueFor, setIssueFor] = useState<PilotItem | null>(null);
  const [addTo, setAddTo] = useState<PilotSection | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    try {
      const b = await client.http.get<PilotBoard>('/v3/rop/pilot/board');
      setBoard(b);
      setErr(null);
    } catch (e: unknown) {
      setErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  const sections = board?.sections ?? [];
  const items = board?.items ?? [];
  const me = board?.me.upn ?? null;
  const byId = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);
  const shown = useMemo(() => filterItems(items, f, me), [items, f, me]);
  const groups = useMemo(() => testerGroups(items), [items]);
  const pilotSections = sections.filter((s) => s.phase.trim() === '1');
  const totals = summaryTotals(pilotSections.filter((s) => s.kind === 'checklist'));
  const mine = items.filter((i) => isAssignedTo(i, me)).length;
  const selected = selectedId == null ? null : items.find((i) => i.id === selectedId) ?? null;
  const searching = !!(f.q || f.status || f.testerGroup || f.mine || f.sectionId !== 'all');

  const isOpen = (s: PilotSection): boolean => open[s.id] ?? (searching ? true : !startsCollapsed(s));

  const replace = (it: PilotItem): void =>
    setBoard((b) => (b ? { ...b, items: b.items.map((x) => (x.id === it.id ? it : x)) } : b));

  async function issueSent(reportId: string | null): Promise<void> {
    const item = issueFor;
    if (!item) return;
    if (!reportId) {
      setNotice(`Your report was sent, but it could not be tied to ${item.code ?? 'the test'} automatically. Set the status to Issue Identified yourself.`);
      return;
    }
    try {
      const r = await client.http.post<{ item: PilotItem }>(`/v3/rop/pilot/items/${item.id}/issues`, { body: { reportId } });
      replace(r.item);
      setRefreshKey((k) => k + 1);
      setNotice(`Report sent and tied to ${item.code ?? 'the test'}. Its change number shows on the test once the tracker files it.`);
      void load();
    } catch (e: unknown) {
      setNotice(`Your report was sent, but tying it to the test failed: ${ApiError.isApiError(e) ? e.message : String(e)}`);
    }
  }

  const kpi = (label: string, value: number | string, tone?: 'ok' | 'warn' | 'err', onClick?: () => void, on?: boolean): JSX.Element => (
    <button type="button" className={`pilot-kpi${tone ? ` pilot-kpi--${tone}` : ''}${on ? ' is-on' : ''}`} onClick={onClick} disabled={!onClick} aria-pressed={on}>
      <span className="pilot-kpi__value">{value}</span>
      <span className="pilot-kpi__label">{label}</span>
    </button>
  );
  const toggleStatus = (s: string) => () => setF((x) => ({ ...x, status: x.status === s ? '' : s }));

  return (
    <section aria-label="Pilot testing">
      {err && <Banner tone="err" title="Couldn't load pilot testing">{err}</Banner>}
      {notice && <Banner tone="info">{notice}</Banner>}
      {!board && !err && <div className="pilot-empty">Loading…</div>}

      {board && (
        <>
          <p className="pilot-muted" style={{ marginTop: 0 }}>
            Consumer pilot (UAT - Consumer): {totals.complete} of {totals.total - totals.notApplicable} complete ({totals.percentComplete}%).
            N/A items are left out of the percentage, as in the workbook.
          </p>
          <div className="pilot-kpis" aria-label="Consumer pilot totals">
            {kpi('Not started', totals.notStarted, undefined, toggleStatus('Not Started'), f.status === 'Not Started')}
            {kpi('In progress', totals.inProgress, undefined, toggleStatus('In Progress'), f.status === 'In Progress')}
            {kpi('Complete', totals.complete, 'ok', toggleStatus('Complete'), f.status === 'Complete')}
            {kpi('Issue identified', totals.issueIdentified, 'err', toggleStatus('Issue Identified'), f.status === 'Issue Identified')}
            {kpi('N/A', totals.notApplicable, undefined, toggleStatus('N/A'), f.status === 'N/A')}
            {kpi('Assigned to me', mine, undefined, () => setF((x) => ({ ...x, mine: !x.mine })), f.mine)}
          </div>

          <div className="pilot-filters" role="search">
            <label className="pilot-field pilot-field--grow">
              Search
              <input type="search" value={f.q} placeholder="ID, words, CHG number, tester"
                onChange={(e) => setF((x) => ({ ...x, q: e.target.value }))} />
            </label>
            <label className="pilot-field">
              Section
              <select value={String(f.sectionId)} onChange={(e) => setF((x) => ({ ...x, sectionId: e.target.value === 'all' ? 'all' : Number(e.target.value) }))}>
                <option value="all">All sections</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="pilot-field">
              Status
              <select value={f.status} onChange={(e) => setF((x) => ({ ...x, status: e.target.value }))}>
                <option value="">Any status</option>
                {board.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value={NO_STATUS}>No status yet</option>
              </select>
            </label>
            <label className="pilot-field">
              Tester group
              <select value={f.testerGroup} onChange={(e) => setF((x) => ({ ...x, testerGroup: e.target.value }))}>
                <option value="">Any group</option>
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <label className="pilot-check">
              <input type="checkbox" checked={f.mine} onChange={(e) => setF((x) => ({ ...x, mine: e.target.checked }))} />
              Assigned to me
            </label>
            {searching && <button type="button" className="pilot-back" onClick={() => setF(EMPTY_PILOT_FILTER)}>Clear filters</button>}
          </div>

          <div className="pilot-summary-wrap">
            <table className="pilot-summary">
              <caption className="pilot-muted" style={{ textAlign: 'left', padding: '8px 8px 0' }}>Summary by section</caption>
              <thead>
                <tr><th>Section</th><th>Not started</th><th>In progress</th><th>Complete</th><th>Issue identified</th><th>N/A</th><th>Total</th><th>% complete</th></tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}{s.phaseNote ? <span className="pilot-muted"> · Phase {s.phase}</span> : null}</td>
                    {s.kind === 'checklist' ? (
                      <>
                        <td>{s.counts.notStarted}</td><td>{s.counts.inProgress}</td><td>{s.counts.complete}</td>
                        <td>{s.counts.issueIdentified}</td><td>{s.counts.notApplicable}</td><td>{s.counts.total}</td>
                        <td>{s.counts.percentComplete}%</td>
                      </>
                    ) : (
                      <td colSpan={7} style={{ textAlign: 'left' }}>{s.counts.total} rows · {s.counts.pass} pass · {s.counts.fail} fail</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sections.filter((s) => f.sectionId === 'all' || s.id === f.sectionId).map((s) => {
            const rows = shown.filter((i) => i.sectionId === s.id);
            if (searching && rows.length === 0) return null;
            const expanded = isOpen(s);
            let lastGroup: string | null = null;
            return (
              <div key={s.id} className={`pilot-section${expanded ? ' is-open' : ''}`}>
                <button type="button" className="pilot-section__head" aria-expanded={expanded}
                  onClick={() => setOpen((o) => ({ ...o, [s.id]: !expanded }))}>
                  <span aria-hidden>{expanded ? '▾' : '▸'}</span>
                  <span className="pilot-section__name">{s.name}</span>
                  {s.phaseNote && <span className="pilot-section__phase">{s.phaseNote}</span>}
                  <span className="pilot-section__counts">
                    {s.kind === 'checklist'
                      ? `${s.counts.complete} of ${s.counts.total} complete · ${s.counts.issueIdentified} issue${s.counts.issueIdentified === 1 ? '' : 's'}`
                      : `${s.counts.total} rows · ${s.counts.pass} pass · ${s.counts.fail} fail`}
                    {searching ? ` · ${rows.length} shown` : ''}
                  </span>
                </button>
                {expanded && (
                  <div className="pilot-section__body">
                    {(s.description || s.canAdd) && (
                      <div className="pilot-section__tools">
                        <span className="pilot-muted">{s.description}</span>
                        {s.canAdd && <Button size="sm" variant="ghost" onClick={() => setAddTo(s)}>Add a test</Button>}
                      </div>
                    )}
                    {rows.length === 0 && <div className="pilot-empty">No items{searching ? ' match these filters' : ''}.</div>}
                    {rows.map((i) => {
                      const head = i.group && i.group !== lastGroup ? i.group : null;
                      lastGroup = i.group;
                      return (
                        <div key={i.id}>
                          {head && <div className="pilot-group">{head}</div>}
                          <button type="button" className="pilot-row" onClick={() => setSelectedId(i.id)}>
                            <span className="pilot-row__key">{i.code ?? '(no ID)'}</span>
                            <span className="pilot-row__main">
                              <span className="pilot-row__title">{itemHeadline(i, s)}</span>
                              <span className="pilot-row__meta" style={{ display: 'block' }}>
                                {[i.area, i.testerGroup, i.channel].filter(Boolean).join(' · ')}
                                {i.assignedTo ? ` · ${i.assignedTo}` : ''}
                                {i.dueDate ? ` · due ${shortDay(i.dueDate)}` : ''}
                                {i.issues.length ? ` · ${i.issues.map((x) => x.changeKey ?? 'report sent').join(', ')}` : ''}
                              </span>
                            </span>
                            <span className="pilot-row__pills">
                              {i.result && <Pill variant={i.result === 'Fail' ? 'err' : 'ok'}>{i.result}</Pill>}
                              {(i.status || s.kind === 'checklist') && <Pill variant={statusTone(i.status)}>{i.status ?? 'Not Started'}</Pill>}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <p className="pilot-muted">
            Risk Management&rsquo;s own sections (pre-launch, control testing, post-implementation) are in the Risk panel.
          </p>
        </>
      )}

      <PilotItemDialog
        item={selected}
        section={selected ? byId.get(selected.sectionId) : undefined}
        statuses={board?.statuses ?? []}
        issueOpen={issueFor !== null}
        refreshKey={refreshKey}
        onClose={() => setSelectedId(null)}
        onSaved={(it) => { replace(it); void load(); }}
        onReportIssue={(it) => { setNotice(null); setIssueFor(it); }}
      />
      <PilotIssueDialog
        item={issueFor}
        section={issueFor ? byId.get(issueFor.sectionId) : undefined}
        onClose={() => setIssueFor(null)}
        onSent={(id) => void issueSent(id)}
      />
      <AddItemDialog
        section={addTo}
        onClose={() => setAddTo(null)}
        onAdded={(it) => { setAddTo(null); setBoard((b) => (b ? { ...b, items: [...b.items, it] } : b)); setSelectedId(it.id); void load(); }}
      />
    </section>
  );
}
